import { ThemeEvents } from '@theme/events';

/**
 * Formats a cent amount using the shop's own money_format string (the same
 * placeholder tokens Shopify themes have always used), so recommendation
 * card prices match the rest of the site exactly instead of guessing at a
 * locale-based format.
 * @param {number} cents
 * @param {string} format
 * @returns {string}
 */
function formatMoney(cents, format) {
  function withDelimiters(number, precision, thousands, decimal) {
    number = (number / 100).toFixed(precision);
    const parts = number.split('.');
    const dollars = parts[0].replace(/(\d)(?=(\d\d\d)+(?!\d))/g, `$1${thousands}`);
    const decimalPart = parts[1] ? decimal + parts[1] : '';
    return dollars + decimalPart;
  }

  const placeholderMatch = format.match(/\{\{\s*(\w+)\s*\}\}/);
  const token = placeholderMatch ? placeholderMatch[1] : 'amount';
  let value;

  switch (token) {
    case 'amount_no_decimals':
      value = withDelimiters(cents, 0, ',', '.');
      break;
    case 'amount_with_comma_separator':
      value = withDelimiters(cents, 2, '.', ',');
      break;
    case 'amount_no_decimals_with_comma_separator':
      value = withDelimiters(cents, 0, '.', ',');
      break;
    case 'amount_with_space_separator':
      value = withDelimiters(cents, 2, ' ', ',');
      break;
    case 'amount_no_decimals_with_space_separator':
      value = withDelimiters(cents, 0, ' ', '');
      break;
    default:
      value = withDelimiters(cents, 2, ',', '.');
  }

  return placeholderMatch ? format.replace(placeholderMatch[0], value) : value;
}

/**
 * "You may also like" — real Shopify recommendations seeded from the most
 * recently added cart item, via the public /recommendations/products.json
 * endpoint. Refreshes whenever the cart changes.
 */
class CartRecommendations extends HTMLElement {
  /** @type {boolean} True once we've actually rendered product cards, so the observer below knows an empty grid means something else wiped it. */
  #hasContent = false;

  connectedCallback() {
    this.grid = this.querySelector('[data-cart-recommendations-grid]');
    this.limit = parseInt(this.dataset.limit, 10) || 4;
    this.onCartUpdate = this.refresh.bind(this);
    document.addEventListener(ThemeEvents.cartUpdate, this.onCartUpdate);

    /**
     * The drawer copy of this element lives inside the header section,
     * which Horizon re-fetches and morphs into place shortly after page
     * load (see sections/header.liquid's lazy hydration script) to sync
     * personalized state. That morph patches our grid back to its
     * server-rendered — and therefore always-empty — markup. Re-render if
     * that happens instead of trying to race or suppress the hydration.
     */
    this.observer = new MutationObserver(() => {
      if (this.#hasContent && this.grid.children.length === 0) {
        this.refresh();
      }
    });
    this.observer.observe(this.grid, { childList: true });

    this.refresh();
  }

  disconnectedCallback() {
    document.removeEventListener(ThemeEvents.cartUpdate, this.onCartUpdate);
    this.observer.disconnect();
  }

  async refresh() {
    try {
      const cart = await (await fetch('/cart.js')).json();

      if (!cart.items.length) {
        this.#hasContent = false;
        this.hidden = true;
        return;
      }

      const inCartProductIds = new Set(cart.items.map((item) => item.product_id));
      const seedProductId = cart.items[cart.items.length - 1].product_id;

      const response = await fetch(
        `/recommendations/products.json?product_id=${seedProductId}&limit=${this.limit + inCartProductIds.size}&intent=related`
      );

      if (!response.ok) {
        this.#hasContent = false;
        this.hidden = true;
        return;
      }

      const data = await response.json();
      const products = (data.products || [])
        .filter((product) => !inCartProductIds.has(product.id))
        .slice(0, this.limit);

      if (!products.length) {
        this.#hasContent = false;
        this.hidden = true;
        return;
      }

      this.#render(products);
      this.hidden = false;
      this.#hasContent = true;
    } catch (error) {
      this.#hasContent = false;
      this.hidden = true;
    }
  }

  /**
   * @param {Array<Record<string, any>>} products
   */
  #render(products) {
    const moneyFormat = this.dataset.moneyFormat || '${{amount}}';

    this.grid.replaceChildren(
      ...products.map((product) => {
        const card = document.createElement('article');
        card.className = 'ka-pcard';

        const media = document.createElement('div');
        media.className = 'ka-pcard__media';

        const imgLink = document.createElement('a');
        imgLink.className = 'ka-pcard__imglink';
        imgLink.href = product.url;
        imgLink.tabIndex = -1;
        imgLink.setAttribute('aria-hidden', 'true');
        if (product.featured_image) {
          const img = document.createElement('img');
          const separator = product.featured_image.includes('?') ? '&' : '?';
          img.src = `${product.featured_image}${separator}width=400`;
          img.alt = '';
          img.loading = 'lazy';
          img.className = 'ka-pcard__img ka-pcard__img--primary';
          img.width = 400;
          img.height = 400;
          imgLink.appendChild(img);
        }
        media.appendChild(imgLink);

        const body = document.createElement('div');
        body.className = 'ka-pcard__body';

        const title = document.createElement('h3');
        title.className = 'ka-pcard__title';
        const titleLink = document.createElement('a');
        titleLink.className = 'ka-pcard__title-link';
        titleLink.href = product.url;
        titleLink.textContent = product.title;
        title.appendChild(titleLink);

        // Same meta row and the same badge formula as snippets/ka-product-card.liquid (D46):
        // floor((compare - price) / compare * 100), whole cents in, never rounded up. Price on the
        // left as "$X+" ("+" only when the price varies), no stars here (this list carries no review data).
        const meta = document.createElement('div');
        meta.className = 'ka-pcard__meta';
        const price = document.createElement('p');
        price.className = 'ka-pcard__price';
        const now = document.createElement('span');
        now.className = 'ka-pcard__now';
        const savePct =
          product.available && product.compare_at_price > product.price
            ? Math.floor(((product.compare_at_price - product.price) * 100) / product.compare_at_price)
            : 0;
        now.textContent = formatMoney(product.price, moneyFormat) + (product.price_varies ? '+' : '');
        price.append(now);
        if (savePct >= 1) {
          price.classList.add('ka-pcard__price--sale');
          const was = document.createElement('s');
          was.className = 'ka-pcard__compare';
          was.textContent = formatMoney(product.compare_at_price, moneyFormat);
          price.append(was);
        }
        meta.append(price);

        // Badges: one spot, top left of the photo, stacked.
        const chips = document.createElement('span');
        chips.className = 'ka-pcard__chips';
        if (!product.available) {
          const chip = document.createElement('span');
          chip.className = 'ka-chip ka-chip--soldout';
          chip.textContent = 'Sold out';
          chips.append(chip);
        }
        if (savePct >= 1) {
          const chip = document.createElement('span');
          chip.className = 'ka-chip ka-chip--sale';
          chip.textContent = `Save ${savePct}%`;
          chips.append(chip);
        }
        if (chips.childElementCount) media.prepend(chips);

        const action = document.createElement('div');
        action.className = 'ka-pcard__action';
        const btn = document.createElement('a');
        btn.className = 'ka-pcard__btn';
        btn.href = product.url;
        btn.textContent = product.available ? (product.variants.length > 1 ? 'Choose options' : 'View') : 'Sold out';
        btn.setAttribute('aria-label', `${btn.textContent}: ${product.title}`);
        action.appendChild(btn);

        body.append(title, meta, action);
        card.append(media, body);

        return card;
      })
    );
  }
}

customElements.define('cart-recommendations', CartRecommendations);
